import React, { useEffect, useState } from "react";
import { Box, Typography, Divider } from "@mui/material";

import { Link } from "react-router-dom";
import FlipCard from "../../components/FlipCard/FlipCard";
import Tooltip from "../../components/Tooltip/Tooltip";
import Colloboration from "../../assets/Colloborate.avif";
import Research from "../../assets/Research.avif";
import Model from "../../assets/img_3dmodeling.jpg";
import cms from "../../assets/img_CMS.jpeg";
import mining from "../../assets/Img_DataMining.png";
import Viz from "../../assets/img_DataVisualizations.jpg";
import map from "../../assets/img_Mapping.png";
import Photogrammetry from "../../assets/img_photogrammetry.jpeg";

const DH = () => {
  useEffect(() => {
    document.title = "What are Digital Humanities? - MS Digital Hub";
  }, []);

  return (
    <Box
      sx={{
        width: "auto",

        padding: "0px 20px",
        margin: "0px auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          marginBottom: "30px",

          textAlign: "center",
          paddingBottom: "5px",
          borderBottom: "2px solid #FFD700",
        }}
      >
        What are "Digital Humanities"?
      </Typography>

      <Typography
        variant="body1"
        sx={{
          marginBottom: "20px",
          lineHeight: "1.8",
          textAlign: "justify",
        }}
      >
        What are the "<b>Digital Humanities</b>"? Imagine the study of the
        humanities integrated with the tools used by STEM (Science, Technology,
        Engineering, and Mathematics). The humanities explore human society and
        culture; this includes history, literature and languages, anthropology
        and archaeology, sociology, philosophy, and the arts. Now combine this
        with the emerging tools and creative new ways of thinking developed by
        STEM fields and you have the basic building blocks of the “Digital
        Humanities.” However, the combination of technology and the study of
        human society and culture is more than the sum of its parts. The digital
        humanities challenge us to think about how best to use technology in
        ways that can make information more accessible without undermining all
        the remarkable achievements that make us human.
      </Typography>
      <Typography
        variant="body1"
        sx={{
          marginBottom: "40px",
          lineHeight: "1.8",
          textAlign: "justify",
        }}
      >
        The digital humanities are everywhere. Blogging, podcasts, interactive
        games, digital exhibits and archive, and video editing, all bridge the
        gap between technology and our lived experiences. At the Hub, our focus
        is on how digital tools, theories, and methods—
        <Tooltip
          keyword="data mining"
          staticData={{
            image: mining,
            imageSource: {
              name: "us.ovhcloud.com",
              url: "https://us.ovhcloud.com/learn/what-is-data-mining/",
            },
            content:
              " Data mining is the process of analyzing a large batch of raw data to identify patterns and extract useful information. It combines computer science and statistical analysis to produce useful information from a gathered dataset.",
          }}
        />
        ,{" "}
        <Tooltip
          keyword="content management"
          staticData={{
            image: cms,
            imageSource: {
              name: "www.mannixmarketing.com",
              url: "https://www.mannixmarketing.com/blog/what-is-a-content-management-system/",
            },
            content:
              "Content management (CM) is the process for collection, delivery, retrieval, governance and overall management of information in any format. The term is typically used in reference to administration of the digital content lifecycle, from creation to permanent storage or deletion.",
            sourceUrl:
              "https://www.techtarget.com/searchcontentmanagement/definition/content-management#:~:text=Content%20management%20(CM)%20is%20the,to%20permanent%20storage%20or%20deletion.",
            sourceName: "Tech Target",
          }}
        />
        ,{" "}
        <Tooltip
          keyword="mapping"
          staticData={{
            image: map,
            imageSource: {
              name: "transcend.io",
              url: "https://transcend.io/blog/data-mapping-basics",
            },
            content:
              "Data mapping is the process of matching fields from one database to another. It's the first step to facilitate data migration, data integration, and other data management tasks.",
            sourceUrl:
              "https://www.talend.com/resources/data-mapping/#:~:text=Data%20mapping%20is%20the%20process,it%20accessible%20to%20decision%20makers.",
            sourceName: "Talend",
          }}
        />
        ,{" "}
        <Tooltip
          keyword="visualizations"
          staticData={{
            image: Viz,
            imageSource: {
              name: "thenewstack.io",
              url: "https://thenewstack.io/7-best-practices-for-data-visualization/",
            },
            content:
              "Data visualization is the graphic representation of information in the form of charts, graphs, maps, and other tools to show trends, patterns, and outliers in data.",
          }}
        />
        ,{" "}
        <Tooltip
          keyword="photogrammetry"
          staticData={{
            image: Photogrammetry,
            imageSource: {
              name: "www.wessexarch.co.uk",
              url: "https://www.wessexarch.co.uk/archaeological-services/photogrammetry",
            },
            content:
              "Photogrammetry is the science of using pictures combined with appropriate software to make 2D and 3D computer models.",
          }}
        />{" "}
        and{" "}
        <Tooltip
          keyword="3D modeling"
          staticData={{
            image: Model,
            imageSource: {
              name: "3dstudio.co",
              url: "https://3dstudio.co/what-is-3d-modeling/",
            },
            content:
              " 3D modeling is a tool that combines art and technology to create detailed, realistic three-dimensional representations of objects and environments, which allows for manipulation and viewing from several angles.",
          }}
        />
        —provide both experts and the public tools to preserve, study, and
        interpret information in creative ways that would be difficult, if not
        impossible, without the use of computers and technology.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "40px",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px 10px",
          margin: "0 auto",
        }}
      >
        <FlipCard
          title="Tools for Research"
          content="DH provides tools to enhance and expand scholarly research and publication."
          icon={Research}
        />
        <FlipCard
          title="Collaboration"
          content="DH fosters collaboration. The Center for Digital Humanities and the Mississippi Digital Humanities Hub are places for those collaborations."
          icon={Colloboration}
        />
      </Box>
      <Box
        sx={{
          marginTop: "30px",
          marginBottom: "30px",
          padding: "20px",
          backgroundColor: "#f9f9f9",
          borderLeft: "5px solid #FFD700",
          borderRadius: "8px",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          fontStyle: "italic",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            marginBottom: "10px",
            lineHeight: "1.8",
            color: "#444",
            textAlign: "justify",
          }}
        >
          “DH is a network of people and projects. There are people who have
          projects that they want to get off the ground, and there are people
          with the skills to facilitate those projects. You don’t need to be a
          computer scientist to do DH. You just need to have a collaborative
          imagination and the creativity to see scholarship as multimodal.”
        </Typography>
        <Typography
          variant="body2"
          sx={{
            textAlign: "right",
            fontWeight: "bold",
            color: "#FFD700",
            marginBottom: "30px",
          }}
        >
          — Phill Howells, Southern Miss Digital Humanities Post-Doc
        </Typography>
      </Box>
      <Typography
        variant="h4"
        sx={{
          marginBottom: "30px",
          textAlign: "center",
        }}
      >
        Will the Digital Humanities enhance your research and preservation
        projects?
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: "20px",
        }}
      >
        <Box
          sx={{
            padding: "20px",
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "scale(1.03)",
              boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
            },
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              marginBottom: "10px",
            }}
          >
            Explore Projects
          </Typography>
          <Typography
            variant="body1"
            sx={{
              lineHeight: "1.8",
              color: "#555",
              textAlign: "justify",
            }}
          >
            One of the best ways to learn more about DH is to look at the
            projects that have already been created within the field. A perfect
            foray into that area comes from Alex Gil’s{" "}
            <Tooltip keyword="Around DH" url="https://arounddh.org/" />. This
            compendium of DH projects from around the world represents a wealth
            of inspiring ideas. The projects span from archival efforts to
            activist extrapolations of global problems and more. We invite you
            to also take a look at some{" "}
            <Tooltip
              keyword="projects"
              url="https://usmcdh.org/cdh-projects/"
            />{" "}
            we have been working on at The University of Southern Mississippi.
          </Typography>
        </Box>

        <Box
          sx={{
            padding: "20px",
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "scale(1.03)",
              boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
            },
            textAlign: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              marginBottom: "10px",
            }}
          >
            Read Theoretical Texts
          </Typography>
          <Typography
            variant="body1"
            sx={{
              lineHeight: "1.8",
              color: "#555",
              textAlign: "justify",
            }}
          >
            Another good way to help yourself understand the fundamentals of DH
            is to read theoretical texts written by pioneers and practitioners
            in the field. Perhaps the best source of these texts comes from{" "}
            <Tooltip
              keyword="Debates in the Digital Humanities"
              url="https://dhdebates.gc.cuny.edu/"
            />
            . <i>Debates</i> is a book series that collates the most respected
            and relevant writing on DH concepts from year to year. The project
            has existed since 2012, meaning that it holds a large backlog of
            interesting articles written on specific as well as general topics.
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          marginTop: "30px",
          marginBottom: "30px",
          backgroundColor: "#ffffff",
          borderRadius: "10px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "20px",
          }}
        ></Box>
      </Box>
    </Box>
  );
};
export default DH;
